import React, { useState, useEffect } from "react";
import axios from "axios";
import { Alert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import SelectField from "../../components/ui/SelectField";
import { toast } from "react-toastify";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";

const ITEMS_PER_PAGE = 8;
const API_URL = process.env.REACT_APP_API_URL + "/util";

const EMPTY_FORM = {
  medico_cod: "",
  tipodcto_cod: "",
  nit: "",
  nombre: "",
  tar_prof: "",
  direccion: "",
  ciudad_cod: "",
  especialidad: "",
};

const MedicosPage = () => {
  const [medicos, setMedicos] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [currentPage, setCurrentPage] = useState(1);
  const [tiposDoc, setTiposDoc] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchMedicos = async () => {
    try {
      const res = await axios.get(`${API_URL}/medicos_all`, { headers });
      setMedicos(res.data);
      setFiltered(res.data);
    } catch (err) {
      setError("Error al obtener médicos");
      toast.error(err.response?.data?.error || err.message);
    }
  };

  useEffect(() => {
    fetchMedicos();
    axios
      .get(`${API_URL}/tipo_dcto`, { headers })
      .then((r) => setTiposDoc(r.data))
      .catch(() => {});
    axios
      .get(`${API_URL}/ciudades`, { headers })
      .then((r) => setCiudades(r.data))
      .catch(() => {});
    axios
      .get(`${API_URL}/medicos_especialidades`, { headers })
      .then((r) => setEspecialidades(r.data))
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    const lower = searchTerm.toLowerCase();
    setFiltered(
      medicos.filter(
        (m) =>
          (m.nombre || "").toLowerCase().includes(lower) ||
          (m.medico_cod || "").toLowerCase().includes(lower) ||
          (m.nit || "").toLowerCase().includes(lower) ||
          (m.especialidad_nombre || "").toLowerCase().includes(lower),
      ),
    );
    setCurrentPage(1);
  }, [searchTerm, medicos]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const current = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const openNew = () => {
    setSelected(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (m) => {
    setSelected(m);
    setForm({
      medico_cod: m.medico_cod || "",
      tipodcto_cod: m.tipodcto_cod || "",
      nit: m.nit || "",
      nombre: m.nombre || "",
      tar_prof: m.tar_prof || "",
      direccion: m.direccion || "",
      ciudad_cod: m.ciudad_cod || "",
      especialidad: m.especialidad || "",
    });
    setShowModal(true);
  };

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.medico_cod || !form.nombre) {
      toast.error("Código y nombre son obligatorios");
      return;
    }
    try {
      if (selected) {
        await axios.put(`${API_URL}/medicos/${selected.medico_cod}`, form, {
          headers,
        });
        toast.success("Médico actualizado correctamente");
      } else {
        await axios.post(`${API_URL}/medicos`, form, { headers });
        toast.success("Médico creado correctamente");
      }
      setShowModal(false);
      fetchMedicos();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API_URL}/medicos/${confirmDelete.medico_cod}`, {
        headers,
      });
      toast.success("Médico eliminado correctamente");
      setConfirmDelete(null);
      fetchMedicos();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    }
  };

  return (
    <div className="space-y-4">
      {error && <Alert type="error">{error}</Alert>}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-400" />
          <input
            type="text"
            placeholder="Buscar médico..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 border border-brand-200 rounded-xl py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <Button onClick={openNew} className="gap-2">
          <Plus className="h-4 w-4" /> Nuevo médico
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-brand-100">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-brand-gradient text-white">
              {[
                "Código",
                "Nombre",
                "NIT",
                "Tipo doc.",
                "Tar. Prof.",
                "Especialidad",
                "Ciudad",
                "",
              ].map((h, i) => (
                <th
                  key={i}
                  className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap ${i === 7 ? "text-right" : "text-left"}`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-50">
            {current.length > 0 ? (
              current.map((m, i) => (
                <tr
                  key={m.medico_cod}
                  className={`transition-colors ${i % 2 === 0 ? "bg-white" : "bg-brand-50/30"} hover:bg-brand-50`}
                >
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded font-semibold">
                      {m.medico_cod}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {m.nombre}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{m.nit || "—"}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {m.tipodcto_cod || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {m.tar_prof || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {m.especialidad_nombre || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {m.ciudad_descrip || "—"}
                  </td>
                  <td className="px-4 py-3 text-right flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(m)}
                      className="gap-1"
                    >
                      <Pencil className="h-3 w-3" /> Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirmDelete(m)}
                      className="gap-1 text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="8"
                  className="py-8 text-center text-gray-400 italic text-sm"
                >
                  No se encontraron médicos.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>
            {current.length} de {filtered.length}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              ← Anterior
            </Button>
            <span className="px-3 py-1.5 bg-brand-50 border border-brand-200 rounded-lg text-brand-700 font-medium">
              {currentPage}/{totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Siguiente →
            </Button>
          </div>
        </div>
      )}

      {/* Modal crear/editar */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-gray-800">
              {selected ? "Editar médico" : "Nuevo médico"}
            </h2>
            <form
              onSubmit={handleSave}
              className="grid grid-cols-1 md:grid-cols-2 gap-3"
            >
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Código *
                </label>
                <input
                  name="medico_cod"
                  value={form.medico_cod}
                  onChange={handleChange}
                  disabled={!!selected}
                  required
                  className="w-full p-2 border rounded-lg text-sm disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Tipo documento
                </label>
                <SelectField
                  name="tipodcto_cod"
                  value={form.tipodcto_cod}
                  onChange={handleChange}
                  placeholder="Seleccione"
                  options={tiposDoc}
                  valueKey="tipodcto_cod"
                  displayFn={(t) => `${t.tipodcto_cod} - ${t.descrip}`}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  NIT
                </label>
                <input
                  name="nit"
                  value={form.nit}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Nombre *
                </label>
                <input
                  name="nombre"
                  value={form.nombre}
                  onChange={handleChange}
                  required
                  className="w-full p-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Tarjeta profesional
                </label>
                <input
                  name="tar_prof"
                  value={form.tar_prof}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Dirección
                </label>
                <input
                  name="direccion"
                  value={form.direccion}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Ciudad
                </label>
                <SelectField
                  name="ciudad_cod"
                  value={form.ciudad_cod}
                  onChange={handleChange}
                  placeholder="Seleccione"
                  options={ciudades}
                  valueKey="ciudad_cod"
                  displayFn={(c) => c.ciudad_descrip}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Especialidad
                </label>
                <SelectField
                  name="especialidad"
                  value={form.especialidad}
                  onChange={handleChange}
                  placeholder="Seleccione"
                  options={especialidades}
                  valueKey="codigo"
                  displayFn={(e) => `${e.codigo} - ${e.descripcion}`}
                />
              </div>
              <div className="col-span-2 flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit">
                  {selected ? "Actualizar" : "Crear"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal confirmar eliminación */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-800">
              Eliminar médico
            </h2>
            <p className="text-sm text-gray-600">
              ¿Confirmas que deseas eliminar al médico{" "}
              <strong>{confirmDelete.nombre}</strong> (
              {confirmDelete.medico_cod})?
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmDelete(null)}>
                Cancelar
              </Button>
              <Button onClick={handleDelete}>Sí, eliminar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicosPage;
